/**
 * ClusterKeywordDropdown
 *
 * Quick Create v2 (#1306) — the clusters-first entry point Chans asked for:
 * "the very first thing should be the dropdown of our clusters where we can
 * select keywords."
 *
 * Lists the tenant's service clusters (the SAME source as the redesigned
 * Keyword Research page — `useServiceClusters`), each titled by name + the date
 * it was last suggested. Selecting a cluster reveals its AVAILABLE (approved)
 * keywords; clicking one adds it to the Target Keywords textarea below via the
 * existing add-callback. Used/queued keywords aren't offered (the backend
 * retires them on content creation).
 *
 * Renders nothing when the tenant has no (non-archived) clusters, so tenants who
 * haven't built clusters yet see the keyword form unchanged.
 */

import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Boxes, Plus, Check } from 'lucide-react';
import { useServiceClusters, useServiceCluster } from '@/hooks/useServiceClusters';
import { availableCount } from '@/services/serviceClusterService';
import { formatNumber } from '@/services/ahrefsService';

export interface ClusterKeywordDropdownProps {
  tenantId: string | null;
  /** Reuses the keyword form's existing single-keyword append (dedupes internally). */
  onAddKeyword: (keyword: string) => void;
  /** Lowercased set of keywords already in the textarea — drives the "Added" state. */
  currentKeywordsSet: Set<string>;
  disabled?: boolean;
}

/** Date-only label for a cluster (last AI-suggest date, else created). */
function clusterDateLabel(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export const ClusterKeywordDropdown: React.FC<ClusterKeywordDropdownProps> = ({
  tenantId,
  onAddKeyword,
  currentKeywordsSet,
  disabled = false,
}) => {
  const [selectedClusterId, setSelectedClusterId] = useState<string>('');

  const { data: listData, isLoading: listLoading } = useServiceClusters(tenantId || '');
  const clusters = (listData?.clusters || []).filter((c) => c.status !== 'archived');

  const { data: cluster, isLoading: keywordsLoading } = useServiceCluster(
    tenantId || '',
    selectedClusterId || null,
  );

  // Nothing to offer until the tenant has clusters — leave the form untouched.
  if (!tenantId || listLoading || clusters.length === 0) return null;

  const available = (cluster?.keywords || []).filter((k) => k.status === 'approved');

  return (
    <div className="space-y-2 mb-4 rounded-lg border border-teal/30 bg-teal-light/40 p-4">
      <Label htmlFor="cluster-select" className="flex items-center gap-2">
        <Boxes className="h-4 w-4 text-teal-dark" />
        Start from a keyword cluster
      </Label>

      <select
        id="cluster-select"
        value={selectedClusterId}
        onChange={(e) => setSelectedClusterId(e.target.value)}
        disabled={disabled}
        className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm disabled:opacity-50"
      >
        <option value="">Select a cluster…</option>
        {clusters.map((c) => {
          const date = clusterDateLabel(c.last_suggested_at || c.created_at);
          const avail = availableCount(c.counts);
          return (
            <option key={c.id} value={c.id}>
              {c.name}
              {date ? ` — ${date}` : ''} ({avail} available)
            </option>
          );
        })}
      </select>

      {selectedClusterId && keywordsLoading && (
        <div className="space-y-1.5 pt-1">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-2/3" />
        </div>
      )}

      {selectedClusterId && !keywordsLoading && available.length === 0 && (
        <p className="text-xs text-muted-foreground pt-1">
          No available keywords left in this cluster — use AI Suggest on Keyword Research to add more.
        </p>
      )}

      {available.length > 0 && (
        <div className="max-h-56 overflow-y-auto space-y-1 pt-1">
          {available.map((k) => {
            const added = currentKeywordsSet.has(k.keyword.toLowerCase());
            return (
              <div
                key={k.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-2.5 py-1.5"
              >
                <div className="min-w-0 flex-1">
                  <span className="text-sm truncate block">{k.keyword}</span>
                  <span className="text-xs text-muted-foreground">
                    Vol {k.search_volume != null ? formatNumber(k.search_volume) : '—'}
                    {' · '}
                    KD {k.keyword_difficulty != null ? k.keyword_difficulty : '—'}
                  </span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={added ? 'ghost' : 'secondary'}
                  disabled={added || disabled}
                  onClick={() => onAddKeyword(k.keyword)}
                  className="flex-shrink-0"
                >
                  {added ? (
                    <>
                      <Check className="h-3.5 w-3.5 mr-1" />
                      Added
                    </>
                  ) : (
                    <>
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Add
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
